import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LogoHome } from '../components/LogoHome';
import { ManualEntryDialog } from '../components/ManualEntryDialog';
import { PendingManualEntries } from '../components/PendingManualEntries';
import { sydLocalInputToUtcIso } from '../utils/sydneyTime';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, LogOut, MapPin, Clock, Plus, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function localISODate(d) {
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-AU', {
    timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit',
  });
}
function csvEscape(s) {
  if (s == null) return '';
  const v = String(s);
  return v.includes(',') || v.includes('"') || v.includes('\n')
    ? `"${v.replace(/"/g, '""')}"` : v;
}

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export default function TimesheetsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const backRoute = user?.role === 'organisation' ? '/admin' : '/supervisor';
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPreset, setManualPreset] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/users`, { headers: authHeader() }).catch(() => ({ data: [] })),
      axios.get(`${API}/clients`, { headers: authHeader() }).catch(() => ({ data: [] })),
    ]).then(([u, c]) => {
      setWorkers((u.data || []).filter(x => ['worker', 'supervisor'].includes(x.role)));
      setClients(c.data || []);
    });
  }, []);

  const openManual = (workerId, dayKey) => {
    let preset = { worker_id: workerId || '' };
    if (dayKey) {
      preset.clock_in = sydLocalInputToUtcIso(`${dayKey}T09:00`);
      preset.clock_out = sydLocalInputToUtcIso(`${dayKey}T12:00`);
    }
    setManualPreset(preset);
    setManualOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timesheets?week_start=${localISODate(weekStart)}`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (delta) => {
    const w = new Date(weekStart); w.setDate(w.getDate() + delta * 7);
    setWeekStart(w);
  };

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart); end.setDate(end.getDate() + 6);
    return `${weekStart.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' })}`;
  }, [weekStart]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [['Worker', 'Date', 'Clock In', 'Clock Out', 'Hours', 'KMs', 'Client', 'Status', 'Entry', 'Reason']];
    for (const w of data.workers) {
      for (const day of data.day_keys) {
        for (const e of w.days[day]) {
          rows.push([
            w.worker_name, day,
            new Date(e.clock_in).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' }),
            e.clock_out ? new Date(e.clock_out).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' }) : '',
            e.hours, e.km, e.client_name || '',
            e.active ? 'ACTIVE' : 'COMPLETED',
            e.manual ? `MANUAL (${(e.approval_status || 'approved').toUpperCase()})` : 'CLOCKED',
            e.manual_reason || '',
          ]);
        }
      }
      // Weekly total row per worker
      rows.push([w.worker_name, 'WEEK TOTAL', '', '', w.week_hours, w.week_km, '', '', '', '']);
    }
    const csv = rows.map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitex_timesheet_${data.week_start}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="timesheets-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoHome className="h-10 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-slate-900 text-sm">Timesheets</h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to={backRoute} className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Week navigation & summary */}
        <Card>
          <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="tsheet-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="tsheet-today">
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="tsheet-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="ml-3 font-semibold text-slate-900" data-testid="tsheet-week-label">{weekLabel}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {data && (
                <>
                  <Badge variant="outline" className="border-slate-300 text-slate-700">
                    <Clock className="h-3 w-3 mr-1" />
                    {data.summary.total_hours}h total
                  </Badge>
                  <Badge variant="outline" className="border-teal-300 text-teal-700">
                    <MapPin className="h-3 w-3 mr-1" />
                    {data.summary.total_km}km total
                  </Badge>
                  <Badge variant="outline" className="border-slate-300 text-slate-700">
                    {data.summary.worker_count} worker{data.summary.worker_count !== 1 ? 's' : ''}
                  </Badge>
                  {data.summary.pending_manual > 0 && (
                    <Badge className="bg-amber-500 text-white" data-testid="tsheet-pending-badge">
                      <ClipboardCheck className="h-3 w-3 mr-1" />{data.summary.pending_manual} pending approval
                    </Badge>
                  )}
                </>
              )}
              <Button onClick={exportCsv} variant="outline" size="sm" disabled={!data} data-testid="tsheet-export">
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button onClick={() => openManual('', null)} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="tsheet-add-manual">
                <Plus className="h-4 w-4 mr-1" /> Manual entry
              </Button>
            </div>
          </CardContent>
        </Card>

        <PendingManualEntries onChanged={load} />

        {/* Grid */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="tsheet-grid">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2 text-slate-600 sticky left-0 bg-slate-100 min-w-[160px]">Worker</th>
                  {(data?.day_keys || []).map((k, i) => {
                    const [yy, mm, dd] = k.split('-').map(Number);
                    return (
                      <th key={k} className="text-left px-2 py-2 text-slate-600 min-w-[130px]">
                        <div className="text-[10px] text-slate-500 uppercase">{DAY_LABELS[i]}</div>
                        <div className="text-slate-800 font-semibold">{dd} {new Date(yy, mm - 1, dd).toLocaleDateString('en-AU', { month: 'short' })}</div>
                      </th>
                    );
                  })}
                  <th className="text-right px-3 py-2 text-slate-600 min-w-[110px]">Week Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400">Loading…</td></tr>
                ) : (data?.workers || []).length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400">No workers found.</td></tr>
                ) : (
                  data.workers.map((w) => (
                    <tr key={w.worker_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tsheet-row-${w.worker_id}`}>
                      <td className="px-3 py-2 sticky left-0 bg-white">
                        <div className="text-slate-900 font-medium">{w.worker_name}</div>
                        <div className="text-[10px] text-slate-400 uppercase">{w.role || ''}</div>
                      </td>
                      {data.day_keys.map((k) => {
                        const entries = w.days[k] || [];
                        return (
                          <td key={k} className="px-1.5 py-1.5 align-top border-l border-slate-100 group/cell" data-testid={`tsheet-cell-${w.worker_id}-${k}`}>
                            {entries.length === 0 ? (
                              <button type="button" onClick={() => openManual(w.worker_id, k)} className="w-full text-left text-slate-300 text-xs pl-1 py-1 rounded hover:bg-teal-50 hover:text-teal-700 transition-colors" title="Add manual entry" data-testid={`tsheet-add-${w.worker_id}-${k}`}>
                                <span className="group-hover/cell:hidden">·</span><span className="hidden group-hover/cell:inline"><Plus className="h-3 w-3 inline" /> add</span>
                              </button>
                            ) : (
                              <div className="space-y-1">
                                {entries.map((e) => (
                                  <div key={e.id} className={`rounded px-1.5 py-1 text-[11px] ${e.active ? 'bg-emerald-50 border border-emerald-300' : e.manual && e.approval_status === 'pending' ? 'bg-amber-50 border border-amber-300' : e.manual && e.approval_status === 'rejected' ? 'bg-slate-50 border border-slate-200 opacity-50 line-through' : e.manual ? 'bg-teal-50/60 border border-teal-200' : 'bg-slate-50 border border-slate-200'}`}
                                    title={e.manual ? `Manual entry by ${e.created_by_name || '—'}${e.approved_by_name ? ` · approved by ${e.approved_by_name}` : ''}\n${e.manual_reason || ''}` : undefined}
                                    data-testid={`tsheet-entry-${e.id}`}>
                                    <div className="text-slate-800 font-medium tabular-nums flex items-center justify-between gap-1">
                                      <span>{fmtTime(e.clock_in)} → {e.clock_out ? fmtTime(e.clock_out) : <span className="text-emerald-700">on-shift</span>}</span>
                                      {e.manual && (
                                        <span className={`text-[9px] font-semibold uppercase px-1 rounded ${e.approval_status === 'pending' ? 'bg-amber-200 text-amber-900' : e.approval_status === 'rejected' ? 'bg-slate-200 text-slate-600' : 'bg-teal-600 text-white'}`} data-testid={`tsheet-manual-tag-${e.id}`}>
                                          {e.approval_status === 'pending' ? 'pending' : e.approval_status === 'rejected' ? 'rejected' : 'manual'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-slate-500 flex items-center gap-2">
                                      <span className="tabular-nums">{e.hours}h</span>
                                      <span className="tabular-nums text-teal-600"><MapPin className="h-2.5 w-2.5 inline" /> {e.km}km</span>
                                    </div>
                                    {e.client_name && <div className="text-slate-400 truncate">{e.client_name}</div>}
                                    {e.manual && e.manual_reason && <div className="text-slate-500 italic truncate">“{e.manual_reason}”</div>}
                                  </div>
                                ))}
                                <button type="button" onClick={() => openManual(w.worker_id, k)} className="hidden group-hover/cell:block w-full text-[10px] text-teal-600 hover:text-teal-800 text-left pl-1" data-testid={`tsheet-add-${w.worker_id}-${k}`}>
                                  <Plus className="h-3 w-3 inline" /> add manual
                                </button>
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right border-l border-slate-100 bg-slate-50">
                        <div className="text-slate-900 font-semibold tabular-nums" data-testid={`tsheet-week-${w.worker_id}`}>
                          {w.week_hours}h
                        </div>
                        {w.pending_hours > 0 && <div className="text-[11px] text-amber-700 tabular-nums" data-testid={`tsheet-pending-${w.worker_id}`}>+{w.pending_hours}h pending</div>}
                        <div className="text-[11px] text-teal-600 tabular-nums">{w.week_km}km</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>

      <ManualEntryDialog open={manualOpen} onOpenChange={setManualOpen} isScheduler workers={workers} clients={clients} preset={manualPreset} onSaved={load} />
    </div>
  );
}
